/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

/**
 * Test runner proxy for the QTI test previewer
 */
import request from 'core/fetchRequest';
import mapHelper from '@oat-sa/tao-test-runner-qti/src/helpers/map';
import ApiError from 'core/error/ApiError';
import { cloneDeep, merge } from 'lodash';

/**
 * The possible states of the test session,
 * coming from the test context
 * (this state comes from the backend)
 */
const testSessionStates = Object.freeze({
    initial: 0,
    interacting: 1,
    modalFeedback: 2,
    suspended: 3,
    closed: 4
});

/**
 * The possible states of an item session,
 * coming from the test context
 */
const itemSessionStates = Object.freeze({
    initial: 0,
    interacting: 1,
    modalFeedback: 2,
    suspended: 3,
    closed: 4,
    solution: 5,
    review: 6
});

/**
 * QTI proxy definition
 * Related to remote services calls
 * @type {Object}
 */
const proxy = {
    name: 'qtiConstructPreviewerProxy',

    /**
     * Installs the proxy
     */
    install() {
        this.makeRequest = ({ url, params, method, body } = {}) => {
            const requestUrl = new URL(url);
            if (params) {
                for (const paramKey of Object.keys(params)) {
                    if (params[paramKey]) {
                        requestUrl.searchParams.set(paramKey, params[paramKey]);
                    }
                }
            }
            const requestOptions = {
                method: method || 'GET',
                jwtTokenHandler: this.jwtTokenHandler
            };
            if (body) {
                if (body instanceof FormData) {
                    requestOptions.body = body;
                } else {
                    requestOptions.body = JSON.stringify(body);
                    requestOptions.headers = {
                        'Content-Type': 'application/json'
                    };
                }
            }
            if (typeof this.requestTimeout === 'number') {
                requestOptions.timeout = this.requestTimeout;
            }
            return request(requestUrl.toString(), requestOptions);
        };

        this.generateTestContext = itemPosition => {
            const currentJump = mapHelper.getJump(this.testMap, itemPosition);
            const currentItem = mapHelper.getItemAt(this.testMap, itemPosition);

            const testContext = {
                itemUri: currentJump.itemUri,
                itemIdentifier: currentJump.identifier,
                itemPosition: currentItem.position,
                itemSessionState: currentItem._proxyData?.itemSessionState ?? 0,
                testPartId: currentJump.part,
                sectionId: currentJump.section,
                state: testSessionStates.interacting,
                options: {},
                allowSkipping: currentItem.allowSkipping,
                validateResponses: currentItem.validateResponses,
                remainingAttempts: currentItem.remainingAttempts
            };
            return testContext;
        };

        this.useAttempt = (testMap, itemIdentifier) => {
            const oldItem = mapHelper.getItem(testMap, itemIdentifier);
            if (oldItem.remainingAttempts > 0) {
                oldItem.remainingAttempts--;
            } else if (oldItem.remainingAttempts === 0) {
                oldItem._proxyData ??= {};
                oldItem._proxyData.itemSessionState = itemSessionStates.closed;
            }
        };

        /**
         * Get info needed to upload a file.
         * Public method and is intended to be called from outside, even though it doesn't belong to proxy interface
         * @public
         * @param {String} itemIdentifier
         * @param {String} responseIdentifier
         * @returns {Promise<Object>} - Promise<{uploadServiceType: 'sandbox'}>
         */
        this.getAttachmentsUploadData = (itemIdentifier, responseIdentifier) => {
            if (!responseIdentifier || !itemIdentifier) {
                throw new Error('Get attachments upload data: missing parameters');
            }
            return Promise.resolve({
                uploadServiceType: 'sandbox'
            });
        };

        /**
         *
         * @param {Object} testMap
         * @param {String} itemUri
         * @returns {Object?}
         */
        this.getItemByUri = (testMap, itemUri) => {
            const jump = mapHelper.getJumps(testMap).find(item => item.itemUri === itemUri);
            return mapHelper.getItemAt(testMap, jump && jump.position);
        };
    },

    /**
     * Initializes the proxy
     * @param {Object} configs - configuration from test runner
     * @param {String} configs.proxy.urls
     * @returns {Promise} - Returns a promise. The proxy will be fully initialized on resolve.
     *                      Any error will be provided if rejected.
     */
    init(configs) {
        this.itemStore = {}; // cache getItem responses; key is item.id
        this.testMap = null; // not from this.getDataHolder, that one may be mutated by the test-runner frontend
        this.portableElements = null;

        // install proxy config
        this.urls = configs.proxy.urls;
        this.params = configs.params;
        this.requestTimeout = configs.requestTimeout;
        this.jwtTokenHandler = configs.jwtTokenHandler;

        return this.makeRequest({
            url: this.urls.init,
            params: {
                testUri: this.params.testUri,
                timer: 1
            }
        }).then(response => {
            const responseData = response.data || {};

            this.testMap = mapHelper.reindex(responseData.testMap);
            this.testMap.jumps.forEach(jump => {
                const item = mapHelper.getItemAt(this.testMap, jump.position);
                jump.itemUri = item.uri;
            });

            // need mapping of itemIdentifier to itemUri to talk to Construct
            this.itemIdsToUris = mapHelper.getJumps(this.testMap)?.reduce(
                (acc, jump) => ({
                    ...acc,
                    [jump.identifier]: jump.itemUri
                }),
                {}
            );

            const currentItem = mapHelper.getItemAt(this.testMap, 0);

            const result = {};

            result.testContext = this.generateTestContext(currentItem.position);
            result.testMap = cloneDeep(this.testMap);
            result.metadata = responseData['delivery-metadata'];
            result.timer = responseData.timer;

            return cloneDeep(result);
        });
    },

    /**
     * Uninstalls the proxy
     * @returns {Promise} - Returns a promise. The proxy will be fully uninstalled on resolve.
     *                      Any error will be provided if rejected.
     */
    destroy() {
        if (this.itemStore) {
            this.itemStore = null;
        }

        // the method must return a promise
        return Promise.resolve();
    },

    /**
     * Gets an item definition by its identifier
     * @param {String} itemIdentifier - The identifier of the item to get
     * @returns {Promise} - Returns a promise. The item data will be provided on resolve.
     *                      Any error will be provided if rejected.
     */
    getItem(itemIdentifier) {
        if (itemIdentifier in this.itemStore) {
            // Load item from store
            const data = this.itemStore[itemIdentifier];
            return Promise.resolve(cloneDeep(data));
        } else {
            // Load from server; Store in store
            const { id } = mapHelper.getItem(this.testMap, itemIdentifier) || {};
            if (!id) {
                throw new ApiError(`There is no item "${itemIdentifier}" in the testMap!`);
            }

            return this.makeRequest({
                url: this.urls.getItem,
                params: {
                    itemUri: this.itemIdsToUris[itemIdentifier],
                    pcis: this.portableElements ? 0 : 1,
                    itemData: 1
                }
            }).then(data => {
                this.portableElements ??= data.portableElements || {};
                data.portableElements = this.portableElements;

                data.itemIdentifier = itemIdentifier;
                data.itemData.data.identifier = itemIdentifier;
                data.itemData.data.attributes.identifier = itemIdentifier;

                data.itemState = {};
                this.itemStore[itemIdentifier] = data;
                return cloneDeep(data);
            });
        }
    },

    /**
     * Submits the state and the response of a particular item
     * @param {String} itemIdentifier - The identifier of the item to update
     * @param {Object} itemState - The state to submit
     * @param {Object} itemResponse - The response object to submit
     * @param {{itemDuration: Number}} otherData
     * @returns {Promise<Object>} - Returns a promise. The result of the request will be provided on resolve.
     *                              Any error will be provided if rejected.
     */
    async submitItem(itemIdentifier, itemState, itemResponse, { itemDuration } = {}) {
        const item = this.itemStore[itemIdentifier];
        item.itemState = cloneDeep(itemState);

        this.useAttempt(this.testMap, itemIdentifier);

        const formData = new FormData();
        formData.append('itemResponse', JSON.stringify(itemResponse));
        formData.append('itemState', JSON.stringify(itemState));
        formData.append('itemDuration', itemDuration);

        const data = await this.makeRequest({
            url: this.urls.submitItem,
            method: 'POST',
            params: {
                itemUri: this.itemIdsToUris[itemIdentifier]
            },
            body: formData
        });
        data.displayFeedbacks = data.displayFeedback;
        if (data.displayFeedbacks) {
            data.feedbacks = item.itemData.data.feedbacks;
        }
        return cloneDeep(data);
    },

    /**
     * Call action on the test
     * @param {string} itemIdentifier - the current item
     * @param {string} action - the action id
     * @param {Object} params
     * @returns {Promise|void} resolves with the response
     */
    callItemAction(itemIdentifier, action, params = {}) {
        const testMap = this.testMap;

        const doMove = () => {
            const oldItem = mapHelper.getItem(testMap, itemIdentifier);
            const oldJump = mapHelper.getJump(testMap, oldItem.position);

            let newPosition;
            let shouldClose = false;

            if (params.direction === 'next') {
                if (params.scope === 'test') {
                    newPosition = testMap.stats.total;
                } else if (params.scope === 'testPart') {
                    newPosition = testMap.parts[oldJump.part].position + testMap.parts[oldJump.part].stats.total;
                } else {
                    newPosition = oldItem.position + 1;
                }
            } else if (params.direction === 'previous') {
                newPosition = oldItem.position - 1;
            } else if (params.direction === 'jump' && Number.isInteger(params.ref)) {
                newPosition = params.ref;
            }

            if (newPosition > testMap.stats.total - 1) {
                shouldClose = true;
            }
            newPosition = Math.max(0, Math.min(testMap.stats.total - 1, newPosition));

            const newTestContext = this.generateTestContext(newPosition);
            if (shouldClose) {
                newTestContext.state = testSessionStates.closed;
            }
            return newTestContext;
        };

        const actions = {
            //simulate backend move action
            move: () => {
                const oldItem = this.itemStore[itemIdentifier];
                oldItem.itemState = cloneDeep(params.itemState);

                this.useAttempt(testMap, itemIdentifier);

                const newTestContext = doMove();
                return { testContext: cloneDeep(newTestContext) };
            },

            skip: () => {
                // FE sends part of state excluding responses (like media play time).
                const oldItem = this.itemStore[itemIdentifier];
                merge(oldItem.itemState, cloneDeep(params.itemState));

                const newTestContext = doMove();
                return { testContext: cloneDeep(newTestContext) };
            }
        };

        if (typeof actions[action] === 'function') {
            return Promise.resolve().then(() => actions[action]());
        } else {
            return Promise.resolve();
        }
    },

    /**
     * Calls an action related to the test
     * @returns {Promise} - Returns a promise. The result of the request will be provided on resolve.
     *                      Any error will be provided if rejected.
     */
    callTestAction() {
        return Promise.resolve();
    }
};

export default proxy;
